import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Tag, Calendar, Gauge, Timer, Flag, Loader2 } from 'lucide-react';
import { fmtDate } from '@/lib/utils';
import { EXPERIENCE_DATA, ADULT_RIG_DATA } from '@/components/booking/bookingDefaults';

const expLabel = (k) => EXPERIENCE_DATA[k]?.name || k;
const rigLabel = (k) => (k === 'child_sim' ? 'Child Sim Rig' : ADULT_RIG_DATA[k]?.name) || k;

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-white/30 text-[10px] font-bold tracking-widest flex items-center gap-1">
      <Icon className="w-3 h-3" /> {label}
    </span>
    <span className="text-white font-bold text-sm">{value || '—'}</span>
  </div>
);

export default function SessionResults({ email }) {
  const { data: results = [], isLoading } = useQuery({
    queryKey: ['session-results', email],
    queryFn: () => base44.entities.SessionResult.filter({ customer_email: email }, '-session_date'),
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-20 border border-white/5 bg-[#111115]">
        <Trophy className="w-10 h-10 text-white/10 mx-auto mb-4" />
        <p className="text-white/30 text-sm">No session results yet</p>
        <p className="text-white/20 text-xs mt-1">Your personal results will appear here after your sessions are processed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {results.map((r) => (
        <div key={r.id} className="bg-[#111115] border border-white/5 p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-white font-bold text-sm flex items-center gap-2">
                <Trophy className="w-4 h-4 text-primary" /> {r.track || 'Session Results'}
              </p>
              <p className="text-white/30 text-xs mt-1 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {r.session_date ? fmtDate(r.session_date) : '—'}
                </span>
                <span>{expLabel(r.experience_type)}</span>
                {r.rig_type && (
                  <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{rigLabel(r.rig_type)}</span>
                )}
              </p>
            </div>
            {r.final_position != null && (
              <span className="bg-primary/10 text-primary text-xs font-bold tracking-wider px-3 py-1.5">P{r.final_position}</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 py-4 border-t border-white/5">
            <Stat icon={Timer} label="FASTEST LAP" value={r.fastest_lap_time} />
            <Stat icon={Timer} label="AVG LAP" value={r.average_lap_time} />
            <Stat icon={Gauge} label="TOP SPEED" value={r.top_speed_kph != null ? `${r.top_speed_kph} km/h` : null} />
            <Stat icon={Flag} label="LAPS" value={r.laps_completed != null ? r.laps_completed : null} />
          </div>
          {r.summary && (
            <p className="text-white/50 text-sm leading-relaxed mt-4 pt-4 border-t border-white/5">{r.summary}</p>
          )}
        </div>
      ))}
    </div>
  );
}